import React from "react";

export default function Demo() {
  type ProfileOutput = {
    name: string;
    level: string;
    score: number;
    primaryDriver: string;
    summary: string;
    actions: string[];
  };

  const mockProfiles: ProfileOutput[] = [
    {
      name: "Competitors",
      level: "MODERATE",
      score: 46,
      primaryDriver: "Rain exposure",
      summary:
        "Moderate environmental load expected. Conditions may affect pacing and surface grip.",
      actions: [
        "Brief competitors on surface conditions.",
        "Monitor footwear recommendations.",
      ],
    },
    {
      name: "Spectators",
      level: "MODERATE",
      score: 43,
      primaryDriver: "Rain exposure",
      summary:
        "Comfort and visibility may be affected in exposed areas.",
      actions: [
        "Communicate shelter locations.",
        "Increase signage visibility if rain persists.",
      ],
    },
    {
      name: "Volunteers",
      level: "MODERATE",
      score: 46,
      primaryDriver: "Rain exposure",
      summary:
        "Extended exposure increases fatigue risk.",
      actions: [
        "Rotate staff in exposed zones.",
        "Ensure waterproof outerwear available.",
      ],
    },
    {
      name: "Infrastructure",
      level: "MODERATE",
      score: 44,
      primaryDriver: "Surface water accumulation",
      summary:
        "Temporary structures and course surfaces require monitoring.",
      actions: [
        "Inspect drainage points.",
        "Monitor timing equipment protection.",
      ],
    },
  ];

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.title}>ASDRY Engine — Sport Event Output</h1>
        <p style={styles.subtitle}>
          Example structured preparation output for a single event scenario.
        </p>
      </header>

      <section style={styles.card}>
        <h2 style={styles.sectionTitle}>Overall Event Assessment</h2>
        <p style={styles.summary}>
          AERS Level: MODERATE
        </p>
        <p>
          Primary Driver: Rain exposure across multiple profiles.
        </p>
        <p>
          Operational posture: Increase monitoring and brief teams ahead of peak conditions.
        </p>
      </section>

      {mockProfiles.map((profile) => (
        <section key={profile.name} style={styles.card}>
          <h3 style={styles.outputTitle}>{profile.name}</h3>
          <p><strong>Level:</strong> {profile.level}</p>
          <p><strong>Primary Driver:</strong> {profile.primaryDriver}</p>
          <p style={{ marginTop: 6 }}><strong>Impact Summary:</strong></p>
          <p>{profile.summary}</p>

          <h4 style={{ marginTop: 12 }}>Recommended Actions</h4>
          <ul>
            {profile.actions.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </section>
      ))}

      <footer style={styles.footer}>
        This public showcase illustrates ASDRY’s structured multi-profile preparation output. Detailed modelling logic and escalation thresholds operate within the full production briefing.
      </footer>
    </main>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    maxWidth: 720,
    margin: "0 auto",
    padding: "32px 16px",
    fontFamily: "Inter, system-ui, sans-serif",
    color: "#0f172a",
  },
  header: { marginBottom: 32 },
  title: { fontSize: "2rem", marginBottom: 8 },
  subtitle: { color: "#475569" },
  card: {
    background: "#ffffff",
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
  },
  cardMuted: {
    background: "#f8fafc",
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  sectionTitle: { fontSize: "1.1rem", marginBottom: 12 },
  button: {
    background: "#1d4ed8",
    color: "#fff",
    padding: "12px 16px",
    borderRadius: 10,
    border: "none",
    fontSize: "1rem",
    cursor: "pointer",
    transition: "background 0.15s ease",
  },
  hint: { fontSize: "0.8rem", color: "#64748b", marginTop: 6 },
  outputTitle: { marginTop: 16, marginBottom: 4, fontWeight: 600 },
  summary: { fontSize: "1.05rem", fontWeight: 600 },
  devToggle: { cursor: "pointer", fontWeight: 600 },
  code: {
    marginTop: 12,
    fontSize: "0.8rem",
    background: "#020617",
    color: "#e5e7eb",
    padding: 12,
    borderRadius: 8,
    overflowX: "auto",
  },
  footer: {
    marginTop: 40,
    fontSize: "0.75rem",
    color: "#64748b",
    textAlign: "center",
  },
  toggleButton: {
    background: "#1d4ed8",
    color: "#fff",
    padding: "8px 14px",
    borderRadius: 999,
    border: "none",
    fontSize: "0.9rem",
    transition: "background 0.15s ease",
  },
  toggleButtonMuted: {
    background: "#e5e7eb",
    color: "#0f172a",
    padding: "8px 14px",
    borderRadius: 999,
    border: "none",
    fontSize: "0.9rem",
  },
};